/**
 * AS Life — brand palette (single source of truth).
 *
 * Mirrors `design_handoff_as_life_portal` (Claude Design handoff).
 * The portal is driven by ONE primary (navy); the rest is a derived ramp.
 * Bronze is the decorative accent. Colour is used as INFORMATION
 * (status / priority / date type), not decoration.
 *
 * These raw values feed `tamagui.config.ts`. Use Tamagui tokens
 * (`$navy9`, `$bronze`, `$tone_blue`, …) in components — not these hexes —
 * unless you need a colour outside the theme (e.g. StatusBar).
 */

// ── Brand: AS Group navy ──────────────────────────────────────────────
export const navy = {
  navy9: '#1D4289', // --primary  (buttons, links, active states)
  navyHover: '#41609C', // mix(primary 84%, white)
  navyActive: '#17356E', // mix(primary 80%, black)
  navyBgLight: '#EBEEF4', // light: mix(primary 9%, white)  — soft accent bg
  navyBgDark: '#1E2C49', // dark:  mix(primary 30%, dark surface bgBase)
  navyBright: '#8EA1C4', // mix(primary 50%, white) — accent on dark chrome
  sidebarTop: '#16265C', // sidebar gradient top
  sidebarBottom: '#101F4C', // sidebar gradient bottom
} as const

// ── Accent: bronze ────────────────────────────────────────────────────
export const bronze = {
  bronze: '#B0863C', // main
  bronzeLight: '#D8BE88',
  bronzeDark: '#7D6A44',
  // brushed metallic gradient (banners / login edge)
  gradient:
    'linear-gradient(90deg,#9c7c47 0%,#d8bd85 18%,#b8945a 36%,#ecd6a2 52%,#b8945a 68%,#d8bd85 84%,#9c7c47 100%)',
} as const

// ── Semantic tones (STATUS = COLOUR) ──────────────────────────────────
export const tones = {
  blue: '#2F6DB5', // in progress / under review / IT
  green: '#2F8F6B', // success / approved / done
  gold: '#B0863C', // attention / in work / corporate date
  red: '#C0584A', // rejected / holiday / termination
  purple: '#1D4289', // brand accent / onboarding
  slate: '#64748B', // neutral default
} as const

// ── Surfaces ──────────────────────────────────────────────────────────
export const surfacesLight = {
  bgLayout: '#FFFFFF',
  bgBase: '#FFFFFF',
  bgElevated: '#FFFFFF',
  border: '#E2E8F0',
  borderSecondary: '#EDF0F4',
  text: '#1A2230', // dark graphite
  muted: '#64748B',
} as const

// Dark = «dimmed» navy-tinted slate (уровень GitHub Dimmed / Discord / Slack), а
// не near-black: почти чёрный фон + почти белый текст дают ~17:1 и «гало» вокруг
// букв. Здесь основной текст ≈12.5:1, muted ≈6.4:1 — см. __tests__/contrast.test.ts.
export const surfacesDark = {
  bgLayout: '#171B24', // page backdrop
  bgBase: '#1E232E', // cards / main surface
  bgElevated: '#262C39', // popovers / hover
  bgPress: '#2E3544', // press / focus
  border: '#394150',
  borderSecondary: 'rgba(255,255,255,0.08)',
  text: '#E2E6EE', // off-white, не #FFF
  muted: '#9CA6B8',
  tertiary: '#7E899C',
} as const

// Читаемый ТЕКСТ тона поверх поверхности темы (→ токены `$tone_<k>_text`,
// их возвращает tone(k).text). Light = исторические значения, dark — осветлённые.
export const toneTextLight = {
  blue: '#2F6DB5',
  green: '#2F8F6B',
  gold: '#9A7430',
  red: '#C0584A',
  purple: '#1D4289',
  slate: '#64748B',
} as const

export const toneTextDark = {
  blue: '#6FA3E0',
  green: '#4FB890',
  gold: '#D2A85A',
  red: '#E5867A',
  purple: '#8EA1C4',
  slate: '#9CA6B8',
} as const
